'use client';

import { useEffect } from 'react';

/**
 * Lightweight pub/sub for agent integration mutations so dependent UI
 * (the integration hub, monetization tab, summary cards) can refresh
 * without full page reloads.
 *
 * Why a window event rather than React context?
 *   - Existing AgentIntegrations is a deeply nested forwardRef component
 *     with many local mutation handlers. Threading a context through
 *     every save site would be invasive and error-prone.
 *   - The mutations cross several React subtrees (the dialog, the
 *     inline integrations section, the monetization tab) that don't
 *     share a single common ancestor without prop drilling.
 *
 * Reasons live in a closed enum so we can grep for every emit/listen
 * site at review time.
 */

export type IntegrationChangedReason =
  | 'platform_capability_change'
  | 'platform_account_bind'
  | 'platform_create'
  | 'platform_delete'
  | 'mcp_save'
  | 'mcp_delete'
  | 'mcp_oauth_return'
  | 'openapi_save'
  | 'openapi_delete'
  | 'media_save'
  | 'media_delete'
  | 'tool_exposure_change'
  | 'connected_account_change'
  | 'manual';

export interface IntegrationChangedDetail {
  reason: IntegrationChangedReason;
  /** Optional provider id or server name that changed. */
  ref?: string;
  /** Optional agentConfigId scope for the event. */
  agentConfigId?: string;
}

const EVENT_NAME = 'agent-integration-changed';

export function dispatchIntegrationChanged(detail: IntegrationChangedDetail): void {
  if (typeof window === 'undefined') return;
  try {
    window.dispatchEvent(
      new CustomEvent<IntegrationChangedDetail>(EVENT_NAME, { detail }),
    );
  } catch {
    // Older browsers without CustomEvent support — fall back silently.
  }
}

/**
 * Subscribe to integration-changed events. Optionally filter by
 * agentConfigId so multi-agent surfaces only refresh when their own
 * agent changes.
 */
export function useIntegrationChanged(
  handler: (detail: IntegrationChangedDetail) => void,
  options?: { agentConfigId?: string },
): void {
  useEffect(() => {
    if (typeof window === 'undefined') return;
    const listener = (e: Event) => {
      const ev = e as CustomEvent<IntegrationChangedDetail>;
      if (!ev.detail) return;
      if (
        options?.agentConfigId &&
        ev.detail.agentConfigId &&
        ev.detail.agentConfigId !== options.agentConfigId
      ) {
        return;
      }
      handler(ev.detail);
    };
    window.addEventListener(EVENT_NAME, listener as EventListener);
    return () => window.removeEventListener(EVENT_NAME, listener as EventListener);
  }, [handler, options?.agentConfigId]);
}
